import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useMemo } from 'react';
import { DepartmentContext } from 'DepartmentWrapper';
import AdminRanksSort from '../AdminRanks/AdminRanksSort';
import Loading from 'Body/Statuses/Loading';
import style from './AdminRankList.style.js';

// Constants
const CONTAINER_STYLES = {
  base: {
    display: 'flex',
    overflowY: 'scroll',
    minHeight: 455,
    height: 'calc(100vh - 420px)',
  },
  grid: {
    width: '100%',
  },
  mobileCard: {
    flexDirection: 'column',
    width: '100%',
  },
};

const TABLE_STYLES = {
  table: {
    margin: '0px',
    height: '100%',
  },
  header: {
    top: -2,
    position: 'sticky',
    verticalAlign: 'middle',
    backgroundColor: 'white',
    zIndex: 3,
    padding: 20,
  },
  headerCell: {
    padding: '5px 10px 5px 0px',
  },
  headerCellCenter: {
    padding: '5px 10px 5px 0px',
    textAlign: 'center',
  },
};

const AdminRankList = ({
  enableRevisedRankScores = false,
  isScoreSortActive = false,
  onCancelRevisedScoreSave,
  onRevisedScoreChange,
  onSortChange,
  sliderValue,
  candidateRanks = [],
  postChange,
  setCandidateRanks,
  isFetchingCandidates,
  isUpdatingRankList,
  isMobile,
  setSelectedUser,
  selectedUser,
  selectedTagFilter,
  mode,
  defaultModes,
  sortDirection,
  sortField,
  setLastJumpTo,
  detailNotesHaveChange,
  viewMode,
}) => {
  const dContext = useContext(DepartmentContext);
  const { season } = dContext || {};

  const renderSortCaret = (field) => {
    if (sortField !== field) {
      return null;
    }

    return (
      <FontAwesomeIcon
        icon={sortDirection === 'asc' ? faCaretUp : faCaretDown}
        style={{ marginLeft: 6 }}
      />
    );
  };

  // Memoized common props for AdminRanksSort
  const sortProps = useMemo(
    () => ({
      candidates: candidateRanks,
      setCandidateRanks,
      setSelectedUser,
      selectedUser,
      sliderValue,
      postChange,
      isFetchingCandidates,
      isUpdatingRankList,
      setLastJumpTo,
      detailNotesHaveChange,
      viewMode,
      selectedTagFilter,
      enableRevisedRankScores,
      isScoreSortActive,
      onCancelRevisedScoreSave,
      onRevisedScoreChange,
      unique_key: 2,
    }),
    [
      candidateRanks,
      setCandidateRanks,
      setSelectedUser,
      selectedUser,
      sliderValue,
      postChange,
      isFetchingCandidates,
      isUpdatingRankList,
      setLastJumpTo,
      detailNotesHaveChange,
      viewMode,
      selectedTagFilter,
      enableRevisedRankScores,
      isScoreSortActive,
      onCancelRevisedScoreSave,
      onRevisedScoreChange,
    ],
  );

  // Table headers configuration
  const tableHeaders = useMemo(
    () => [
      { label: 'Rank Order', style: TABLE_STYLES.headerCellCenter },
      { label: 'Flag', style: TABLE_STYLES.headerCell },
      { label: 'Candidate', style: TABLE_STYLES.headerCellCenter },
      { label: 'Interview Date', style: TABLE_STYLES.headerCell },
      {
        label: mode === defaultModes.prescreen ? 'Do Not Interview' : 'Do Not Rank',
        style: TABLE_STYLES.headerCell,
      },
      {
        label: mode === defaultModes.prescreen ? 'Prescreen Score' : 'Final Score',
        style: TABLE_STYLES.headerCell,
        field: 'CandidateFinalScoreAsDecimal',
        isSortable: true,
      },
      ...(enableRevisedRankScores
        ? [
            {
              label: 'Revised Score',
              style: TABLE_STYLES.headerCell,
              field: 'RevisedScore',
              isSortable: true,
            },
          ]
        : []),
    ],
    [mode, defaultModes, enableRevisedRankScores],
  );

  // Loading state
  if (isFetchingCandidates && (!candidateRanks || candidateRanks.length <= 0)) {
    return (
      <div style={{ paddingTop: '20vh' }}>
        <Loading options={{ labelText: 'Fetching Rank List...' }} />
      </div>
    );
  }

  // Grid view
  if (viewMode === 'Grid') {
    return (
      <div style={CONTAINER_STYLES.grid}>
        {candidateRanks?.length > 0 ? (
          <AdminRanksSort {...sortProps} isCollapsed />
        ) : isFetchingCandidates ? (
          <div style={{ marginTop: '20vh' }}>
            <Loading options={{ labelText: 'Fetching Rank List...' }} />
          </div>
        ) : (
          <div style={{ textAlign: 'center', fontWeight: 'bold' }}>No Items</div>
        )}
      </div>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <div id="rankListContainer_List" style={CONTAINER_STYLES.base}>
        <div style={CONTAINER_STYLES.mobileCard}>
          <AdminRanksSort {...sortProps} isCard={true} />
        </div>
      </div>
    );
  }

  // Desktop table view
  return (
    <div id="rankListContainer_List" style={CONTAINER_STYLES.base}>
      <table className="table table-striped table-hover" style={TABLE_STYLES.table}>
        <thead style={TABLE_STYLES.header}>
          <tr>
            {tableHeaders.map((header, index) => (
              <th key={`header_${index}`} style={header.style}>
                {header.isSortable ? (
                  <button
                    type="button"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      onSortChange(header.field);
                    }}
                  >
                    {header.label}
                    {renderSortCaret(header.field)}
                  </button>
                ) : (
                  header.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        {candidateRanks?.length > 0 ? (
          <AdminRanksSort {...sortProps} />
        ) : (
          <tbody>
            <tr>
              <td colSpan={enableRevisedRankScores ? 7 : 6} style={{ fontWeight: 'bold', textAlign: 'center' }}>
                No Items
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  );
};

export default AdminRankList;
